// File: src/pages/AboutPage.jsx
import { useState, useEffect } from 'react';
import { useStats } from '../hooks/useMeetings';
import { toPersian } from '../data/constants';

/* ══════════════════════════════════════════════════
   کامپوننت انیمیشن شمارش اعداد (از ۰ تا عدد هدف)
══════════════════════════════════════════════════ */
const AnimatedNumber = ({ target, duration = 2000 }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTimestamp = null;
    const finalValue = parseInt(target, 10) || 0;

    if (finalValue === 0) {
      setCount(0);
      return;
    }

    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      // افکت Ease-out برای کند شدن نرم در انتهای شمارش
      const easeOut = 1 - Math.pow(1 - progress, 4); 
      setCount(Math.floor(easeOut * finalValue));

      if (progress < 1) {
        window.requestAnimationFrame(step);
      } else {
        setCount(finalValue);
      }
    };

    window.requestAnimationFrame(step);
  }, [target, duration]);

  return toPersian(count);
};

/* ══════════════════════════════════════════════════
   Sub-Components (Section, StatBig)
══════════════════════════════════════════════════ */
const Section = ({ icon, title, children }) => (
  <div style={{ 
    background: 'var(--bg2)', 
    border: '1px solid var(--border)', 
    borderRadius: 18,
    padding: '18px 20px', 
    marginBottom: 14,
    boxShadow: '0 2px 5px rgba(0,0,0,0.01)'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
      <span style={{ fontSize: 22 }}>{icon}</span>
      <h3 style={{ fontSize: 15, fontWeight: 950, color: 'var(--text)' }}>{title}</h3>
    </div>
    <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.9 }}>{children}</div>
  </div>
);

const StatBig = ({ value, label, color = 'var(--accent)' }) => (
  <div style={{ 
    background: 'var(--bg2)', 
    border: '1px solid var(--border)', 
    borderRadius: 16,
    padding: '16px 12px', 
    textAlign: 'center',
    boxShadow: '0 2px 5px rgba(0,0,0,0.01)',
    position: 'relative',
    overflow: 'hidden'
  }}>
    {/* یک هاله رنگی محو در پس‌زمینه کارت آمار برای زیبایی بیشتر */}
    <div style={{ position: 'absolute', top: '-20%', left: '-20%', width: '140%', height: '140%', background: `radial-gradient(circle, ${color}11 0%, transparent 70%)`, pointerEvents: 'none' }} />
    <div style={{ fontSize: 28, fontWeight: 950, color, lineHeight: 1, direction: 'ltr' }}>
      <AnimatedNumber target={value} />
    </div>
    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6, fontWeight: 700 }}>{label}</div>
  </div>
);

/* ══════════════════════════════════════════════════
   Main Page Component
══════════════════════════════════════════════════ */
export default function AboutPage() {
  const { data: stats, isLoading, error } = useStats();

  if (isLoading) {
    return (
      <div style={{ paddingBottom: 8, direction: 'rtl' }} className="fade-in">
        <div className="skeleton" style={{ height: 160, borderRadius: 22, marginBottom: 20 }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20 }}>
          <div className="skeleton" style={{ height: 80, borderRadius: 16 }} />
          <div className="skeleton" style={{ height: 80, borderRadius: 16 }} />
          <div className="skeleton" style={{ height: 80, borderRadius: 16 }} />
        </div>
        <div className="skeleton" style={{ height: 160, borderRadius: 18, marginBottom: 14 }} />
        <div className="skeleton" style={{ height: 260, borderRadius: 18 }} />
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--red)', fontSize: 13, fontWeight: 700 }} className="fade-in">
        خطا در دریافت اطلاعات آماری از سرور. لطفاً اتصال خود را بررسی کنید.
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: 8, direction: 'rtl' }} className="fade-in">

      {/* 🔵 بخش Hero */}
      <div style={{
        background: 'var(--hero-grad)',
        border: '1px solid var(--border)', 
        borderRadius: 22, 
        padding: '28px 22px',
        textAlign: 'center', 
        marginBottom: 20, 
        position: 'relative', 
        overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -30, left: -30, width: 120, height: 120, background: 'rgba(14,165,233,.06)', borderRadius: '50%', filter: 'blur(30px)' }} />
        <div style={{ position: 'absolute', bottom: -20, right: -20, width: 100, height: 100, background: 'rgba(16,185,129,.06)', borderRadius: '50%', filter: 'blur(25px)' }} />
        
        <div style={{ position: 'relative' }}>
          <div style={{ fontSize: 44, marginBottom: 12 }}>🔵</div>
          <h1 style={{ fontSize: 22, fontWeight: 950, marginBottom: 6, lineHeight: 1.35, color: 'var(--hero-title)' }}>
            انجمن الکلی‌های گمنام ایران
          </h1>
          <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.7, maxWidth: 340, margin: '0 auto', fontWeight: 500 }}>
            AA ایران — یک برادری از مردان و زنانی که تجربه، قدرت و امید خود را با یکدیگر به اشتراک می‌گذارند
          </p>
        </div>
      </div>

      {/* 📊 بخش آمار متحرک */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20 }}>
        <StatBig value={stats.total_meetings} label="جلسه فعال" color='var(--accent)' />
        <StatBig value={stats.province_count} label="استان" color='var(--green)' />
        <StatBig value={stats.zone_count} label="ناحیه" color='var(--purple)' />
      </div>

      {/* 🌟 بخش درباره AA (طراحی جدید و جذاب‌تر) */}
      <div style={{
        background: 'linear-gradient(145deg, var(--bg2), var(--bg3))',
        border: '1px solid var(--border)',
        borderRadius: 20,
        padding: '24px',
        marginBottom: 16,
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 4px 15px rgba(0,0,0,0.02)'
      }}>
        {/* واترمارک نقل قول */}
        <div style={{ position: 'absolute', top: 5, left: 15, fontSize: 120, color: 'var(--text)', opacity: 0.03, fontFamily: 'serif', lineHeight: 1, pointerEvents: 'none' }}>
          ❞
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, position: 'relative' }}>
          <div style={{ background: 'var(--bg3)', padding: 8, borderRadius: 12, display: 'flex' }}>
            <span style={{ fontSize: 22 }}>🌟</span>
          </div>
          <h3 style={{ fontSize: 16, fontWeight: 950, color: 'var(--text)' }}>درباره انجمن AA</h3>
        </div>
        
        <div style={{ fontSize: 13.5, color: 'var(--text2)', lineHeight: 2.1, fontWeight: 500, textAlign: 'justify', position: 'relative' }}>
          {stats.about_text ? (
            stats.about_text.split('\n').map((line, i) => (
              <p key={i} style={{ marginBottom: 10 }}>{line}</p>
            ))
          ) : (
            <p>الکلی‌های گمنام یک انجمن جهانی از زنان و مردانی است که برای بهبودی تلاش می‌کنند...</p>
          )}
        </div>
      </div>

      {/* 📅 انواع جلسات */}
      <Section icon="📅" title="انواع جلسات انجمن">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            { icon: '🏠', title: 'جلسات حضوری', desc: 'برگزاری در مکان‌های فیزیکی و ساختارمند در سراسر کشور' },
            { icon: '💻', title: 'جلسات مجازی', desc: 'برگزاری آنلاین و برخط از طریق پلتفرم‌های ارتباطی مجهز' },
            { icon: '👔', title: 'جلسات آقایان', desc: 'مخصوص حضور و تبادل تجربه برادران هم‌درد' },
            { icon: '👗', title: 'جلسات بانوان', desc: 'مخصوص حضور و تبادل تجربه خواهران هم‌درد' },
            { icon: '👥', title: 'جلسات مختلط', desc: 'آزاد برای حضور تمامی اعضای انجمن بدون محدودیت جنسیت' },
          ].map(item => (
            <div key={item.title} style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 12,
              background: 'var(--bg3)', 
              border: '1px solid var(--border)',
              borderRadius: 14, 
              padding: '12px 14px',
              transition: 'transform 0.2s ease',
            }}>
              <span style={{ fontSize: 24, flexShrink: 0, background: 'var(--bg2)', padding: 6, borderRadius: 10 }}>{item.icon}</span>
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 900, color: 'var(--text)' }}>{item.title}</div>
                <div style={{ fontSize: 11.5, color: 'var(--text2)', marginTop: 4 }}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section icon="💡" title="دوازده قدم AA">
        برنامه دوازده قدم AA یک راهنمای معنوی و کاملاً کاربردی برای بهبودی پایدار از اعتیاد به الکل است. این برنامه پویا بر پایه اصول اخلاقی نظیر صداقت، فروتنی، پذیرش مسئولیت شخصی و خدمت صادقانه به دیگر هم‌دردان بنا شده است.
      </Section>

      <Section icon="🤝" title="سنت‌های دوازده‌گانه">
        دوازده سنت، اصول حیاتی و راهنمای کلان AA برای حفظ اصالت، وحدت و سلامت ساختار داخلی انجمن هستند. این سنت‌ها چارچوبی امن برای کار گروهی و تعامل سالم و بدون حاشیه بین اعضا و گروه‌ها فراهم می‌کنند.
      </Section>

      <Section icon="🔒" title="گمنامی و محرمانگی">
        گمنامی سنگ بنای سنتی و معنوی تمام ابعاد AA است. هر آنچه در جلسات گفته می‌شود، جزیی از حریم خصوصی اعضا بوده و در همان‌جا می‌ماند. هویت فردی اعضا در سطح رسانه‌ها و عموم همیشه کاملاً محرمانه نگه داشته می‌شود.
      </Section>

      {/* 📞 بنر تماس */}
      <div style={{
        background: 'var(--banner-grad)',
        border: '1px solid var(--banner-border)', 
        borderRadius: 20, 
        padding: '24px 20px', 
        marginBottom: 14,
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 36, marginBottom: 10 }}>📞</div>
        <h3 style={{ fontSize: 16, fontWeight: 950, color: 'var(--text)', marginBottom: 8 }}>نیاز به کمک یا گفتگو دارید؟</h3>
        <p style={{ fontSize: 12.5, color: 'var(--text2)', marginBottom: 18, lineHeight: 1.8, maxWidth: 360, margin: '0 auto 18px' }}>
          اگر خود یا عزیزانتان با مشکل مصرف الکل دست‌و‌پنجه نرم می‌کنید، در جلسات AA با آغوش باز خوش‌آمد هستید.
        </p>

        {stats.contact_phone && (
          <a 
            href={`tel:${stats.contact_phone}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              background: 'var(--green)',
              color: '#ffffff',
              padding: '12px 28px',
              borderRadius: 14,
              fontSize: 15,
              fontWeight: 900,
              textDecoration: 'none',
              marginBottom: 16,
              boxShadow: '0 6px 16px rgba(16, 185, 129, 0.25)',
              transition: 'transform 0.1s'
            }}
          >
            <span>تماس با خط راهنما</span>
            <span style={{ fontSize: 13, background: 'rgba(0,0,0,0.15)', padding: '2px 8px', borderRadius: 8, letterSpacing: 1 }}>{toPersian(stats.contact_phone)}</span>
          </a>
        )}

        <div style={{ fontSize: 11.5, color: 'var(--banner-footer)', fontWeight: 800 }}>
          هیچ حق عضویتی وجود ندارد · گمنامی شما کاملاً محفوظ است
        </div>
      </div>

      {/* 🖨️ فوتر */}
      <div style={{ textAlign: 'center', padding: '12px 0', fontSize: 11, color: 'var(--text3)', fontWeight: 600 }}>
        اپلیکیشن جامع راهنمای جلسات AA ایران · نسخه ۲.۰
      </div>
    </div>
  );
}